import React, { ChangeEvent, useCallback, useEffect } from 'react'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { useReactiveVar } from '@apollo/client'
import { difference, intersectionWith } from 'lodash'
import { useTranslation } from 'react-i18next'
import { SingleSettingSection } from '../common/SingleSettingSection'
import { ProjectVersionFragment } from '../../generated/graphql'
import { projectVar, selectedVersionsVar } from '../../apollo/reactive-vars'
import { sortByProperty } from '../../util/array-utility'
import { VersionCalculator } from '../../calculators/VersionCalculator'
import { useCalculator } from '../../calculators/use-calculator'

export type SelectedVersion = ProjectVersionFragment & {
  /** Energy calculator for entire version */
  energyCalculator: VersionCalculator | null
}

export function VersionsSelector(): React.ReactElement {
  const project = useReactiveVar(projectVar)
  const { t } = useTranslation()
  const selectedVersions = useReactiveVar(selectedVersionsVar)
  const { getCalculatedEnergies } = useCalculator()

  const mapVersionToSelectedVersion = useCallback(
    (version: ProjectVersionFragment): SelectedVersion => ({
      ...version,
      energyCalculator: getCalculatedEnergies(project, version),
    }),
    [getCalculatedEnergies, project],
  )

  useEffect(() => {
    if (!project) {
      if (selectedVersionsVar().length > 0) {
        selectedVersionsVar([])
      }
      return
    }

    const defaultVersion = project.versions.find((version) => version.isDefault)
    if (!defaultVersion) {
      throw new Error('Default version not found')
    }

    const previouslySelectedVersions = selectedVersionsVar()
    const selectedIntersection = intersectionWith(
      previouslySelectedVersions,
      project.versions,
      (a, b) => a.id === b.id,
    )
    if (selectedIntersection.length === 0) {
      selectedVersionsVar([mapVersionToSelectedVersion(defaultVersion)])
    }
  }, [project, mapVersionToSelectedVersion])

  if (!project) {
    return <></>
  }

  const onSelectionChange = (
    _: ChangeEvent<unknown>,
    newVersions: ProjectVersionFragment[],
  ) => {
    const newVersionIds = newVersions.map((version) => version.id)

    const oldSelectedVersions = selectedVersionsVar()
    const oldSelectedVersionIds = oldSelectedVersions.map(
      (version) => version.id,
    )

    const addedVersionIds = difference(newVersionIds, oldSelectedVersionIds)
    const addedSelectedVersions = project.versions
      .filter((version) => addedVersionIds.includes(version.id))
      .map(mapVersionToSelectedVersion)

    const remainingSelectedVersions = oldSelectedVersions.filter((version) =>
      newVersionIds.includes(version.id),
    )

    const newSelectedVersions = sortByProperty(
      [...remainingSelectedVersions, ...addedSelectedVersions],
      'name',
    )

    selectedVersionsVar(newSelectedVersions)
  }

  const sortedOptions = sortByProperty(project.versions, 'name')

  return (
    <SingleSettingSection
      title={t('common.versionsSelector.showing')}
      omitPadding
    >
      <Autocomplete
        options={sortedOptions}
        getOptionLabel={(version) => version.name}
        getOptionSelected={(option, value) => option.id === value.id}
        value={selectedVersions}
        onChange={onSelectionChange}
        renderInput={(params) => <TextField {...params} />}
        multiple
      />
    </SingleSettingSection>
  )
}
